// When the kitchen board should make a sound, and how old a ticket is.
//
// The board used to beep on every `order_updated` the server broadcast — eleven different
// events, eight of them money (a discount, an offer, a comp, a price override, and each of
// their undos) that put nothing on the board. A drink-only send beeped the kitchen for a
// ticket that went to the bar. And a new order beeped twice: once on the event, again when
// the refetch found the count had grown. A pass that hears "new ticket" every time someone
// applies a discount learns to ignore the sound, and then misses a real one.
//
// So the board beeps when what the kitchen must act on changes, and only then: a ticket
// appears, or a ticket already up gains, loses or changes a line, a note, an allergy or a
// message. Not when only its status moves — that is the kitchen's own bump.

import { onStation } from '@verlo/shared/lib/kitchenRoutes';

/** What a ticket asks of the kitchen, ignoring where it is in the kitchen's own flow. The
 * table's allergy is part of it: one that arrives while the ticket is on the board (a guest
 * mentions it after ordering) is the change a kitchen most needs to hear.
 *
 * `only` is a station's key on a station's own screen: the grill shows the lines it works
 * on, anywhere on their route, and a word about the rest, so a note changed on a fryer's line is not a reason to sound the
 * grill. What is said to the whole table (a message, the allergy) still is. */
export const ticketSignature = (t, only = null) => JSON.stringify([
  (t.items || []).filter((l) => !only || onStation(l, only)).map((l) => [
    l.line_id || l.name, Number(l.quantity) || 0, Boolean(l.voided),
    (l.notes || '').trim(), (l.allergy_notes || '').trim(),
    (l.allergen_clash || []).join(','), (l.allergen_unread || []).join(','),
  ]),
  (t.messages || []).map((m) => m.id || `${m.kind}:${m.text}`),
  (t.allergens || []).join(','), (t.allergy_notes || '').trim(), Boolean(t.has_allergies),
]);

/** `{ id: signature }` for the board as it stands. */
export const boardSignatures = (tickets, only = null) => Object.fromEntries(
  (tickets || []).map((t) => [t.id, ticketSignature(t, only)]),
);

/** True when the board now asks the kitchen for something it did not before. `previous`
 *  is null on the first load, which is never a reason to beep — that is a screen opening. */
export const boardNeedsAttention = (previous, tickets, only = null) => {
  if (!previous) return false;
  return (tickets || []).some((t) => previous[t.id] !== ticketSignature(t, only));
};

/** Whole minutes since `iso`, against a clock the board ticks itself. */
export const minutesSince = (iso, now) => Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));

/** "2 on Fry · 1 on the pass": the rest of a ticket, for a station's screen. Quantities,
 * by station, in the venue's station order with the pass last. */
export function elsewhereWords(lines, stations = []) {
  const count = {};
  for (const l of lines || []) {
    const key = l?.station || 'kitchen';
    count[key] = (count[key] || 0) + (Number(l?.quantity) || 1);
  }
  const order = [...(stations || []).map((s) => s.key), 'kitchen'];
  const name = (key) => (key === 'kitchen' ? 'the pass' : (stations || []).find((s) => s.key === key)?.name || key);
  return Object.keys(count)
    .sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99))
    .map((key) => `${count[key]} on ${name(key)}`)
    .join(' · ');
}
